import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import IncognitoGuard from './components/IncognitoGuard';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import AccessDenied from './pages/AccessDenied';
import StockList from './pages/inventory/StockList';
import ItemsList from './pages/items/ItemsList';
import ItemForm from './pages/items/ItemForm';
import ItemDetail from './pages/items/ItemDetail';
import CategoryList from './pages/categories/CategoryList';
import CategoryForm from './pages/categories/CategoryForm';
import Profile from './pages/Profile';
import CustomerList from './pages/customers/CustomerList';
import CustomerForm from './pages/customers/CustomerForm';
import CustomerOrders from './pages/customers/CustomerOrders';
import OutboundOrderCreate from './pages/customers/OutboundOrderCreate';
import OutboundOrderEdit from './pages/customers/OutboundOrderEdit';
import InboundOrderCreate from './pages/customers/InboundOrderCreate';
import CustomerOrderView from './pages/customers/CustomerOrderView';
import SupplierList from './pages/suppliers/SupplierList';
import SupplierForm from './pages/suppliers/SupplierForm';
import SupplierOrders from './pages/suppliers/SupplierOrders';
import SupplierOrderView from './pages/suppliers/SupplierOrderView';
import SupplierInboundOrderCreate from './pages/suppliers/SupplierInboundOrderCreate';
import ShipmentList from './pages/shipments/ShipmentList';
import ShipmentView from './pages/shipments/ShipmentView';
import UserList from './pages/users/UserList';
import UserForm from './pages/users/UserForm';
import UserItemLimits from './pages/users/UserItemLimits';
import ChangePassword from './pages/ChangePassword';
import LapsedUserReport from './pages/reports/LapsedUserReport';
import InventoryMovement from './pages/reports/InventoryMovement';
import InventoryMovementV2 from './pages/reports/InventoryMovementV2';
import ItemMovements from './pages/reports/ItemMovements';
import SalesGraphReport from './pages/reports/SalesGraphReport';
import PendingItemsReport from './pages/reports/PendingItemsReport';

function App() {
  return (
    <IncognitoGuard>
      <Routes>
        {/* Public Route - Outside of Layout */}
        <Route path="/login" element={<Login />} />

      <Route path="/" element={<Layout />}>
        {/* Access Denied Route */}
        <Route path="access-denied" element={<AccessDenied />} />

  {/* Dashboard - Protected */}
  {/* Redirect root to /dashboard so eligible users land on the dashboard URL */}
  <Route index element={<Navigate to="/dashboard" replace />} />
  {/* Also expose dashboard at /dashboard */}
  <Route path="dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        
        {/* Customer Routes - Protected */}
        <Route path="customers" element={<ProtectedRoute><CustomerList /></ProtectedRoute>} />
        <Route path="customers/new" element={<ProtectedRoute><CustomerForm mode="create" /></ProtectedRoute>} />
        <Route path="customers/:id" element={<ProtectedRoute><CustomerForm mode="view" /></ProtectedRoute>} />
        <Route path="customers/:id/edit" element={<ProtectedRoute><CustomerForm mode="edit" /></ProtectedRoute>} />
        <Route path="customers/orders" element={<ProtectedRoute><CustomerOrders /></ProtectedRoute>} />
        <Route path="customers/orders/:id" element={<ProtectedRoute><CustomerOrderView /></ProtectedRoute>} />
        <Route path="customers/orders/outbound/new" element={<ProtectedRoute><OutboundOrderCreate /></ProtectedRoute>} />
        <Route path="customers/orders/outbound/:id/edit" element={<ProtectedRoute><OutboundOrderEdit /></ProtectedRoute>} />
        <Route path="customers/orders/inbound/new" element={<ProtectedRoute><InboundOrderCreate /></ProtectedRoute>} />

        {/* Supplier Routes - Protected */}
        <Route path="suppliers" element={<ProtectedRoute><SupplierList /></ProtectedRoute>} />
        <Route path="suppliers/new" element={<ProtectedRoute><SupplierForm mode="create" /></ProtectedRoute>} />
        <Route path="suppliers/:id" element={<ProtectedRoute><SupplierForm mode="view" /></ProtectedRoute>} />
        <Route path="suppliers/:id/edit" element={<ProtectedRoute><SupplierForm mode="edit" /></ProtectedRoute>} />
        <Route path="suppliers/orders" element={<ProtectedRoute><SupplierOrders /></ProtectedRoute>} />
        <Route path="suppliers/orders/:id" element={<ProtectedRoute><SupplierOrderView /></ProtectedRoute>} />
        <Route path="suppliers/orders/inbound/new" element={<ProtectedRoute><SupplierInboundOrderCreate /></ProtectedRoute>} />

        {/* Shipments Routes - Protected */}
        <Route path="customers/shipments" element={<ProtectedRoute><ShipmentList /></ProtectedRoute>} />
        <Route path="customers/shipments/:id" element={<ProtectedRoute><ShipmentView /></ProtectedRoute>} />

        {/* Items Routes - Protected */}
        <Route path="items" element={<ProtectedRoute><ItemsList /></ProtectedRoute>} />
        <Route path="items/new" element={<ProtectedRoute><ItemForm mode="create" /></ProtectedRoute>} />
        <Route path="items/stock" element={<ProtectedRoute><StockList /></ProtectedRoute>} />
        <Route path="items/:id" element={<ProtectedRoute><ItemDetail /></ProtectedRoute>} />
        <Route path="items/:id/edit" element={<ProtectedRoute><ItemForm mode="edit" /></ProtectedRoute>} />

        {/* Category Routes - Protected (Admin Only) */}
        <Route path="categories" element={<ProtectedRoute><CategoryList /></ProtectedRoute>} />
        <Route path="categories/new" element={<ProtectedRoute><CategoryForm mode="create" /></ProtectedRoute>} />
        <Route path="categories/:id/edit" element={<ProtectedRoute><CategoryForm mode="edit" /></ProtectedRoute>} />

        {/* User Management Routes - Protected (Admin Only) */}
        <Route path="users" element={<ProtectedRoute><UserList /></ProtectedRoute>} />
        <Route path="users/new" element={<ProtectedRoute><UserForm mode="create" /></ProtectedRoute>} />
        <Route path="users/:id/edit" element={<ProtectedRoute><UserForm mode="edit" /></ProtectedRoute>} />
        <Route path="users/:id/item-limits" element={<ProtectedRoute><UserItemLimits /></ProtectedRoute>} />

        {/* User Profile - Protected */}
        <Route path="profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />

        {/* Reports Routes - Protected */}
        <Route path="reports/lapsed-users" element={<ProtectedRoute><LapsedUserReport /></ProtectedRoute>} />
        <Route path="reports/inventory-movement" element={<ProtectedRoute><InventoryMovement /></ProtectedRoute>} />
        <Route path="reports/inventory-movement-v2" element={<ProtectedRoute><InventoryMovementV2 /></ProtectedRoute>} />
        <Route path="reports/item-movements" element={<ProtectedRoute><ItemMovements /></ProtectedRoute>} />
        <Route path="reports/sales-graph" element={<ProtectedRoute><SalesGraphReport /></ProtectedRoute>} />
        <Route path="reports/pending-items" element={<ProtectedRoute><PendingItemsReport /></ProtectedRoute>} />
      </Route>
    </Routes>
    </IncognitoGuard>
  );
}

export default App;